// Turn the backend's ChartCommand into a grounded, confirmable proposal. GROUNDING happens against the
// LIVE per-chart options in app_context (from the dashboard) — a value a chart doesn't actually offer, or
// a property on the wrong chart, becomes a clarification, never an action. Otto always CONFIRMS before
// changing a chart, ALWAYS names WHICH chart, and speaks the dashboard's CURRENT language.
import { groundChange, allowedLabelsFor, chartsSupporting, chartDisplayName, chartBaseName, chartLabelOf, prettyValue, optionsFor, SCALAR_PROPERTIES, isValidScalar } from "./chartCatalog";

const PROPERTY_NOUN = {
    chart_type: "Chart type",
    category: "Grouping",
    sort_by: "Sorting",
    top_n: "Top N",
    dimension: "Dimension",
    view: "View",
    export: "Export",
    value_mode: "Measure"
};

// ---------------------------------------------------------------------------------------------------
// EVERY chart Otto names comes from THIS page's app_context — never from the static CHART_LABELS map.
//
// The map only covers the FCA dashboard's two charts, and this module used to hardcode it into the
// clarifying questions and their option chips. The chart RESOLUTION was page-correct all along
// (`chartsSupporting` reads app_context), so on the Asset Dashboard Otto worked out the right supporters
// and then asked "…the Yearly Breakdown chart, the Overall Cost chart, or both?" — offering two charts that
// are not on the page while the four that are went unmentioned. Only the SENTENCE was wrong, which is why
// nothing failed: the grounding tests all passed.
//
// Anything user-visible that names a chart must go through these helpers.
// ---------------------------------------------------------------------------------------------------

const sentenceCase = text => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

// A chart named for a CHOICE list: the page's label without the live-grouping suffix chartDisplayName adds
// (a suffix is informative mid-sentence, noise in a list of options).
const chartChoiceName = (appContext, chart) => {
    const label = chartLabelOf(appContext, chart);
    return label ? `the ${label}` : "the chart";
};

// "the A or the B" / "the A, the B, or the C" — for "which one?" questions, where picking several is not
// on offer.
const chartOrList = (appContext, charts) => {
    const names = (charts || []).map(c => chartChoiceName(appContext, c));
    if (!names.length) return "the chart";
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} or ${names[1]}`;
    return `${names.slice(0, -1).join(", ")}, or ${names[names.length - 1]}`;
};

// "the A, the B, or both?" / "the A, the B, the C, or all of them?" — when applying to every listed chart
// IS on offer. Two charts keep the original "both" wording; more take "all of them".
const chartChoiceQuestion = (appContext, charts) =>
    `${(charts || []).map(c => chartChoiceName(appContext, c)).join(", ")}, or ${charts.length === 2 ? "both" : "all of them"}?`;

// The tappable chips for that question — bare labels (no leading "the"), plus the apply-to-all answer.
const chartChoiceOptions = (appContext, charts) => [
    ...(charts || []).map(c => chartLabelOf(appContext, c) || c),
    charts.length === 2 ? "Both" : "All of them"
];

const clarifyFallback = appContext => {
    const charts = Object.keys((appContext && appContext.charts) || {});
    if (!charts.length) return "Which chart would you like to change?";
    return `Which chart would you like to change — ${chartOrList(appContext, charts)}?`;
};

// Human phrasing for one validated change, in the dashboard's CURRENT language (names the chart + shows
// the value it's changing FROM). "instead of X" only when X differs from the new value.
export const describeChartChange = (appContext, change, current = {}) => {
    const { chart, property, value } = change;
    const val = prettyValue(appContext, chart, property, value);
    if (property === "chart_type") {
        const from = current.chart_type && current.chart_type !== value ? ` from a ${prettyValue(appContext, chart, property, current.chart_type)}` : "";
        return `Change ${chartDisplayName(appContext, chart, current)}${from} to a ${val}`;
    }
    if (property === "category") {
        // Flat name here (no live-grouping suffix): "Group the yearly breakdown chart by Trade" — the suffix
        // would name the OLD grouping we're replacing and read contradictorily.
        const name = chartBaseName(appContext, chart);
        // The CSP chart groups on two axes; for a hierarchy (display-axis) grouping the "instead of X" clause
        // would compare against the OTHER axis and mislead, so suppress it there.
        const opt = optionsFor(appContext, chart, property).find(o => o.value === value);
        const isDisplayAxis = !!(opt && opt.axis === "display");
        const from = !isDisplayAxis && current.category && current.category !== value ? ` instead of ${prettyValue(appContext, chart, property, current.category)}` : "";
        return `Group ${name} by ${val}${from}`;
    }
    if (property === "sort_by") {
        const from = current.sort_by && current.sort_by !== value ? ` instead of ${prettyValue(appContext, chart, property, current.sort_by)}` : "";
        return `Sort ${chartDisplayName(appContext, chart, current)} by ${val}${from}`;
    }
    if (property === "top_n") {
        return `Limit ${chartDisplayName(appContext, chart, current)} to the top ${String(value).trim()}`;
    }
    if (property === "dimension") {
        return `Show ${chartDisplayName(appContext, chart, current)} in ${val}`;
    }
    if (property === "view") {
        return `${value === "minimize" ? "Minimize" : "Maximize"} ${chartDisplayName(appContext, chart, current)}`;
    }
    if (property === "export") {
        return `Export ${chartDisplayName(appContext, chart, current)} as ${val}`;
    }
    return `Update ${chartDisplayName(appContext, chart, current)}`;
};

const clarify = (base, message, options) => ({ ...base, chart: true, needsClarification: true, changes: [], message, options: options || [] });

// Clarifying question for a change that failed grounding — steering to the chart(s) that CAN do it.
const clarifyForFailure = (appContext, ground, change) => {
    const noun = PROPERTY_NOUN[change.property] || "That";
    if (SCALAR_PROPERTIES.has(change.property) && ground.reason === "bad_value") {
        return { message: `${noun} needs a positive number.`, options: [] };
    }
    if (ground.reason === "bad_value") {
        // The chart HAS this property but not this value. If another chart offers the value, steer there
        // (e.g. "only the Annual chart can group by Building"); otherwise list this chart's real options.
        const elsewhere = chartsSupporting(appContext, change.property, change.value).filter(c => c !== change.chart);
        if (elsewhere.length) {
            const right = chartDisplayName(appContext, elsewhere[0]);
            const val = prettyValue(appContext, elsewhere[0], change.property, change.value);
            const verb = change.property === "category" ? `group by ${val}` : `use ${val}`;
            return { message: `Only ${right} can ${verb} — ${chartDisplayName(appContext, change.chart)} can't. Use ${right}?`, options: [] };
        }
        const opts = allowedLabelsFor(appContext, change.chart, change.property);
        const name = chartDisplayName(appContext, change.chart);
        return { message: opts.length ? `That isn't an option for ${name}. Choose ${opts.join(" or ")}.` : `${name} can't do that.`, options: opts };
    }
    // wrong_chart / no_chart: which chart(s) have this property at all?
    const supporters = chartsSupporting(appContext, change.property);
    if (supporters.length === 1) {
        const right = chartDisplayName(appContext, supporters[0]);
        const wrong = chartLabelOf(appContext, change.chart) ? ` (not ${chartDisplayName(appContext, change.chart)})` : "";
        return { message: `${noun} applies to ${right}${wrong} — did you mean ${right}?`, options: [] };
    }
    return { message: clarifyFallback(appContext), options: [] };
};

// Property-specific phrasing for the "which chart?" clarification when the user named no chart.
const unspecifiedQuestion = (appContext, change, chart) => {
    const label = prettyValue(appContext, chart, change.property, change.value);
    if (change.property === "chart_type") return `Which chart should I show as a ${label}`;
    if (change.property === "sort_by") return `Which chart should I sort by ${label}`;
    if (change.property === "dimension") return `Which chart should I show in ${label}`;
    if (change.property === "top_n") return `Which chart should I limit to the top ${String(change.value).trim()}`;
    if (change.property === "view") return `Which chart should I ${change.value === "minimize" ? "minimize" : "maximize"}`;
    if (change.property === "export") return `Which chart should I export as ${label}`;
    if (change.property === "value_mode") return `Which chart should I show by ${label}`;
    return `Which chart should I group by ${label}`; // category (default)
};

// COMPOUND grouping ("group A by B" — two dimensions at once). ONLY the CSP by Cost chart has two grouping
// axes: a hierarchy `display` (Building/Site/Region/Project/Additions — its options are tagged axis:"display")
// AND a series `horizontal_chart_type` (Category/Trade/…). So a two-dimension grouping is CSP-ONLY — assign
// the hierarchy dim to the display axis and the breakdown dim to the series axis. The Annual chart has a
// single grouping axis, so it can't do "A by B". This is the "suggest the only available option" path.
const resolveCompoundGrouping = (base, appContext, values, namedChart) => {
    // Two-axis grouping is a property of the FCA dashboard's CSP chart specifically. A page without that
    // chart has nothing to resolve here, and must not be answered with its name.
    if (!chartLabelOf(appContext, "csp")) return clarify(base, clarifyFallback(appContext), []);
    const cspCatOpts = optionsFor(appContext, "csp", "category");
    const known = values.filter(v => cspCatOpts.some(o => o.value === v));
    const isDisplayDim = v => (cspCatOpts.find(o => o.value === v) || {}).axis === "display";
    const labelOf = v => prettyValue(appContext, "csp", "category", v);
    const displayDims = known.filter(isDisplayDim);
    const seriesDims = known.filter(v => !isDisplayDim(v));
    // Fits the CSP chart only as exactly ONE hierarchy dim + ONE breakdown dim.
    if (displayDims.length === 1 && seriesDims.length === 1) {
        const changes = [
            { chart: "csp", property: "category", value: displayDims[0] },
            { chart: "csp", property: "category", value: seriesDims[0] }
        ];
        const pair = `${labelOf(displayDims[0])} and ${labelOf(seriesDims[0])}`;
        const cspName = chartDisplayName(appContext, "csp"); // "the overall cost chart"
        const message = namedChart === "annual_by_category"
            ? `Only ${cspName} can group by ${pair} at once — group it by ${pair}?`
            : `Group ${cspName} by ${pair}?`;
        return { ...base, chart: true, needsClarification: false, changes, message };
    }
    // Two dims on the SAME axis (two hierarchies, or two breakdowns) — the CSP chart has ONE slot per axis.
    return clarify(
        base,
        `${sentenceCase(chartChoiceName(appContext, "csp"))} groups by one hierarchy (Building/Site/Region/Project/Additions) and one breakdown (Category/Trade/…) at a time — name one of each.`,
        []
    );
};

// interpretResponse = { request_id, session_id, intent_kind, chart: ChartCommand }; ctx = { utterance, appContext }.
// The model only CAPTURES intent (which chart the user named, or null for a grouping). WHICH chart a
// grouping actually applies to is resolved HERE, deterministically, from the live per-chart options — so
// availability is never left to the model's judgement.
export const buildChartProposal = (interpretResponse, ctx = {}) => {
    const appContext = ctx.appContext;
    const base = {
        requestId: interpretResponse && interpretResponse.request_id,
        sessionId: interpretResponse && interpretResponse.session_id,
        utterance: ctx.utterance
    };
    const currentOf = chart => ((appContext && appContext.charts && appContext.charts[chart] && appContext.charts[chart].current) || {});
    const command = (interpretResponse && interpretResponse.chart) || {};
    const rawChanges = command.changes || [];

    // Model asked to clarify (no concrete property to change, e.g. "change the chart").
    if (command.needs_clarification || rawChanges.length === 0) {
        const c = command.clarification;
        const q = (c && c.question) || command.assistant_message || clarifyFallback(appContext);
        const opts = (c && c.options) || [];
        return clarify(base, opts.length ? `${q} (${opts.join(" / ")})` : q, opts);
    }

    // COMPOUND grouping ("group A by B") = 2+ DISTINCT category dims aimed at ONE destination (all chart=null,
    // or all the same named chart). Resolve it up front (CSP-only) so we never clarify on just one dim and
    // silently drop the other. Same value on 2 charts is the "both" pattern (handled below), not a compound.
    const categoryChanges = rawChanges.filter(c => c.property === "category");
    const catValues = [...new Set(categoryChanges.map(c => c.value))];
    const catCharts = new Set(categoryChanges.map(c => (c.chart == null ? null : c.chart)));
    if (categoryChanges.length >= 2 && catValues.length >= 2 && catCharts.size === 1) {
        return resolveCompoundGrouping(base, appContext, catValues, [...catCharts][0]);
    }

    // A value requested on 2+ charts (the "both" pattern) — an unavailable one is skipped, not clarified.
    const sameValueCount = (property, value) => rawChanges.filter(c => c.property === property && c.value === value).length;

    const applied = [];
    const skips = []; // human notes: a chart skipped because it doesn't offer the value
    for (const change of rawChanges) {
        // No chart named (chart=null): resolve by REAL availability across charts, uniformly for EVERY
        // property (grouping, visualization, sort, dimension, Top N) — one supporter => apply there, two =>
        // clarify Annual/CSP/Both, none => clarify. The model never decides which chart; the frontend does.
        if (change.chart == null) {
            if (SCALAR_PROPERTIES.has(change.property) && !isValidScalar(change.property, change.value)) {
                return clarify(base, `${PROPERTY_NOUN[change.property] || "That"} needs a positive number.`, []);
            }
            const supporters = chartsSupporting(appContext, change.property, change.value);
            if (supporters.length === 1) {
                applied.push({ chart: supporters[0], property: change.property, value: change.value });
                continue;
            }
            if (supporters.length >= 2) {
                // Ask about the charts that ACTUALLY support this change on THIS page, named in its own
                // vocabulary — the supporters were already correct; the sentence is what leaked.
                return clarify(
                    base,
                    `${unspecifiedQuestion(appContext, change, supporters[0])} — ${chartChoiceQuestion(appContext, supporters)}`,
                    chartChoiceOptions(appContext, supporters)
                );
            }
            // NO chart offers it. Say so — and say what IS on offer — instead of asking "which chart?" about
            // a change none of them can make.
            //
            // Only `category` used to take this path; every other option-catalog property fell through to a
            // generic "which chart would you like to change?" that then listed charts unable to do the thing.
            // Nothing reached it while every option was a standing one. FCA-7053 changed that: Pie became
            // conditional on End of Life being grouped, so "show me charts as pie" on an ungrouped Asset
            // Dashboard suddenly had zero supporters and Otto offered all six charts for a view none of them
            // could take. Scalars keep their own numeric phrasing above.
            if (!SCALAR_PROPERTIES.has(change.property)) {
                const holder = chartsSupporting(appContext, change.property)[0];
                const opts = holder ? allowedLabelsFor(appContext, holder, change.property) : [];
                if (change.property === "category") {
                    const hint = opts.length ? ` ${sentenceCase(chartChoiceName(appContext, holder))} groups by ${opts.slice(0, 6).join(", ")}…` : "";
                    return clarify(base, `No chart can group by that.${hint}`, []);
                }
                // No chart offers the value, so prettyValue has no option to read a label from and hands back
                // the raw token ("pie") — sentence-case it so the refusal doesn't read like a typo.
                const valueLabel = sentenceCase(holder ? prettyValue(appContext, holder, change.property, change.value) : change.value);
                const hint = opts.length ? ` ${sentenceCase(chartChoiceName(appContext, holder))} offers ${opts.join(" or ")}.` : "";
                const lead =
                    change.property === "chart_type"
                        ? `No chart here can be shown as a ${valueLabel}.`
                        : `${PROPERTY_NOUN[change.property] || "That"} isn't available on any chart here.`;
                return clarify(base, `${lead}${hint}`, opts);
            }
            return clarify(base, clarifyFallback(appContext), []);
        }
        // Explicit chart: ground it against that chart's live options (or scalar rule).
        const g = groundChange(appContext, change);
        if (g.ok) {
            applied.push({ chart: change.chart, property: change.property, value: change.value });
            continue;
        }
        // "both"-style request where THIS chart just can't take the value -> skip it and explain.
        if (g.reason === "bad_value" && sameValueCount(change.property, change.value) > 1) {
            const sup = chartsSupporting(appContext, change.property, change.value);
            const valueLabel = sup.length ? prettyValue(appContext, sup[0], change.property, change.value) : change.value;
            skips.push(`${chartDisplayName(appContext, change.chart)} has no ${valueLabel} grouping`);
            continue;
        }
        // A single change on a chart that can't do it -> steer to the chart that can.
        const { message, options } = clarifyForFailure(appContext, g, change);
        return clarify(base, message, options);
    }

    if (applied.length === 0) return clarify(base, clarifyFallback(appContext), []);

    let message = applied.map(change => describeChartChange(appContext, change, currentOf(change.chart))).join(", and ");
    if (skips.length) message += ` (${skips.join("; ")}, so skipped)`;
    return { ...base, chart: true, needsClarification: false, changes: applied, message: message + "?" };
};
